import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fmtINR } from "@/lib/api";
import { useProfiles } from "@/lib/profiles";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const STATUS_COLORS = {
  draft: "text-neutral-400",
  sent: "text-blue-400",
  accepted: "text-emerald-400",
  rejected: "text-red-400",
  converted: "text-[#D4AF37]",
};

export default function Quotations() {
  const { profiles, activeProfileId, setActiveProfileId } = useProfiles();
  const profileId = activeProfileId || profiles[0]?.id;
  const [quotations, setQuotations] = useState([]);
  const [clients, setClients] = useState([]);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    if (!profileId) { setQuotations([]); setClients([]); return; }
    const [q, cl] = await Promise.all([
      api.get(`/quotations?profile_id=${profileId}`),
      api.get(`/clients?profile_id=${profileId}`),
    ]);
    setQuotations(q.data);
    setClients(cl.data);
  }, [profileId]);

  useEffect(() => { load(); }, [load]);

  const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";

  const remove = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm("Delete this quotation?")) return;
    await api.delete(`/quotations/${id}`);
    toast.success("Quotation deleted");
    load();
  };

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-6 lg:py-10">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 lg:mb-12">
        <div>
          {profiles.length > 1 && (
            <Select value={profileId} onValueChange={setActiveProfileId}>
              <SelectTrigger className="bg-transparent border border-white/20 rounded-none text-white font-mont w-full sm:w-56 mb-3 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10 text-white rounded-none">
                {profiles.map((p) => (<SelectItem key={p.id} value={p.id} className="font-mont text-sm">{p.name}</SelectItem>))}
              </SelectContent>
            </Select>
          )}
          <p className="font-mont text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">— Estimates</p>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white">Quotations</h1>
        </div>
        <Button
          data-testid="create-quotation-top-btn"
          onClick={() => navigate("/quotations/new")}
          className="rounded-none bg-white text-black hover:bg-neutral-200 font-sans px-6 py-5 w-full sm:w-auto"
        >
          <Plus className="w-4 h-4 mr-2" strokeWidth={2} />
          New Quotation
        </Button>
      </div>

      <div className="border border-white/10">
        <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-3 border-b border-white/10 font-mont text-[10px] uppercase tracking-[0.25em] text-neutral-500">
          <div className="col-span-2">Quotation No</div>
          <div className="col-span-3">Client</div>
          <div className="col-span-2">Date</div>
          <div className="col-span-2 text-right">Amount</div>
          <div className="col-span-2 text-right">Status</div>
          <div className="col-span-1 text-right">—</div>
        </div>
        {quotations.length === 0 ? (
          <div className="py-20 text-center font-mont text-xs uppercase tracking-[0.3em] text-neutral-600">
            No quotations yet
          </div>
        ) : (
          quotations.map((q) => (
            <Link
              key={q.id}
              to={`/quotations/${q.id}`}
              data-testid={`quotation-row-${q.quotation_number}`}
              className="block md:grid md:grid-cols-12 gap-4 px-4 py-4 md:py-5 md:items-center border-b border-white/10 hover:bg-white/[0.02] transition-colors"
            >
              {/* Mobile card layout */}
              <div className="flex md:hidden items-start justify-between mb-1">
                <div>
                  <div className="font-mont text-sm text-[#D4AF37]">{q.quotation_number}</div>
                  <div className="text-sm text-white mt-0.5">{clientName(q.client_id)}</div>
                  <div className="font-mont text-xs text-neutral-500 mt-1">{q.issue_date}</div>
                </div>
                <button
                  data-testid={`delete-quotation-${q.quotation_number}`}
                  onClick={(e) => remove(q.id, e)}
                  className="text-neutral-600 hover:text-red-400 transition-colors p-1"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
              <div className="flex md:hidden items-center justify-between font-mont text-sm">
                <span className="text-white">{fmtINR(q.totals?.grand_total)}</span>
                <span className={`uppercase text-xs tracking-widest ${STATUS_COLORS[q.status] || "text-neutral-400"}`}>{q.status}</span>
              </div>

              {/* Desktop grid layout */}
              <div className="hidden md:block col-span-2 font-mont text-sm text-[#D4AF37]">{q.quotation_number}</div>
              <div className="hidden md:block col-span-3 text-sm text-white truncate">{clientName(q.client_id)}</div>
              <div className="hidden md:block col-span-2 font-mont text-xs text-neutral-400">{q.issue_date}</div>
              <div className="hidden md:block col-span-2 text-right font-mont text-sm text-white">{fmtINR(q.totals?.grand_total)}</div>
              <div className="hidden md:block col-span-2 text-right font-mont text-xs uppercase tracking-widest">
                <span className={STATUS_COLORS[q.status] || "text-neutral-400"}>{q.status}</span>
              </div>
              <div className="hidden md:flex col-span-1 justify-end">
                <button
                  data-testid={`delete-quotation-${q.quotation_number}-desktop`}
                  onClick={(e) => remove(q.id, e)}
                  className="text-neutral-600 hover:text-red-400 transition-colors"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            </Link>
          ))
        )}
      </div>
    </div>
  );
}
